import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, ChevronLeft, ChevronRight, ShieldCheck, Truck, Lock, FlaskConical, MessageSquare,
    Sparkles, Tag, Headset, Microscope, Beaker, Quote, Package2, Award, FileCheck2,
    Grid3x3, ChevronDown, Search, Brain, Activity, Layers,
} from "lucide-react";
import { MolecularBg } from "../components/MolecularBg";
import { VortexOrb } from "../components/VortexOrb";
import { HexaGrid } from "../components/HexaGrid";
import { DNAHelix } from "../components/DNAHelix";
import { StarRating } from "../components/StarRating";
import { AnnouncementBanner } from "../components/AnnouncementBanner";
import { MascotShowcase } from "../components/MascotShowcase";
import { MobileCarbonHero } from "../components/MobileCarbonHero";
import { ShopGateway } from "../components/ShopGateway";
import { useSEO } from "../lib/useSEO";
import api from "../lib/api";
import { toast } from "sonner";

/* Icon lookup used by CMS-editable sections */
const ICONS = {
    truck: Truck, sparkles: Sparkles, tag: Tag, headset: Headset, package: Package2,
    award: Award, flask: FlaskConical, lock: Lock, shield: ShieldCheck, grid: Grid3x3,
    beaker: Beaker, microscope: Microscope, brain: Brain, activity: Activity, layers: Layers,
    search: Search, fileCheck: FileCheck2,
};
const IconOf = (name) => ICONS[name] || Sparkles;

/* Map category slug → visual icon (used by the CMS-free category cards). */
const CATEGORY_ICON = {
    recovery: ShieldCheck,
    "glp-research": FlaskConical,
    cognitive: Brain,
    longevity: Sparkles,
    "research-stacks": Layers,
    "lab-supplies": Microscope,
    accessories: Package2,
    "coa-library": FileCheck2,
};

/* ───────────────────────────── Hero Carousel ───────────────────────────── */

const HeroCarousel = ({ slides }) => {
    const list = (slides || []).filter((s) => s.active !== false);
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (paused || list.length <= 1) return;
        const id = setInterval(() => setIndex((i) => (i + 1) % list.length), 6500);
        return () => clearInterval(id);
    }, [paused, list.length]);

    if (list.length === 0) return null;

    const goTo = (i) => setIndex((i + list.length) % list.length);

    return (
        <section
            className="relative overflow-hidden pt-20 cfibre"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            data-testid="hero-carousel"
        >
            {/* Layered carbon-fibre + cyan illumination backdrop */}
            <div className="absolute inset-0 z-0" style={{
                background: "radial-gradient(ellipse 80% 50% at 78% 45%, rgba(0,229,255,0.14), transparent 60%), radial-gradient(ellipse 60% 50% at 20% 80%, rgba(0,229,255,0.06), transparent 55%)"
            }} />
            <div className="absolute inset-0 z-0 opacity-[0.35]" style={{
                background: "repeating-linear-gradient(45deg, rgba(255,255,255,0.02) 0 2px, rgba(0,0,0,0.4) 2px 4px, rgba(255,255,255,0.015) 4px 6px, rgba(0,0,0,0.35) 6px 8px)",
                backgroundSize: "6px 6px",
            }} />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#080808] z-[1]" />

            <div className="container-x relative z-10 py-10 md:py-16 lg:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-center min-h-[70vh]">
                    {/* LEFT — headline column */}
                    <div className="lg:col-span-6 order-2 lg:order-1">
                        {list.map((s, i) => (
                            <div
                                key={s.key}
                                className={`transition-all duration-700 ${i === index ? "opacity-100 translate-x-0 relative" : "opacity-0 -translate-x-6 absolute pointer-events-none"}`}
                                data-testid={`hero-slide-${s.key}`}
                                aria-hidden={i !== index}
                            >
                                {/* Numbered tech eyebrow */}
                                <div className="flex items-center gap-3 mb-6">
                                    <span className="tech-label" style={{ color: "#00E5FF" }}>01 / {s.eyebrow || "Precision Engineered"}</span>
                                    <div className="cyan-line flex-1 max-w-[80px]" />
                                </div>

                                <h1
                                    className="text-metal-gold mb-6"
                                    style={{
                                        fontFamily: "'Archivo Black', 'Space Grotesk', Impact, sans-serif",
                                        fontWeight: 900,
                                        fontSize: "clamp(2.6rem, 6.6vw, 5.8rem)",
                                        lineHeight: 0.94,
                                        letterSpacing: "-0.01em",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {s.headline_top}
                                    {s.headline_accent && (
                                        <><br /><span style={{ color: "#00E5FF", textShadow: "0 0 30px rgba(0,229,255,0.55)" }}>{s.headline_accent}</span></>
                                    )}
                                </h1>

                                {/* SCIENCE · DISCIPLINE · RESULTS strip */}
                                <div className="flex items-center gap-3 md:gap-5 mb-7 text-[10px] md:text-[11px] font-mono uppercase tracking-[0.28em] text-[var(--silver-mid)]">
                                    <span>Science</span>
                                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--cyan)] shadow-[0_0_8px_var(--cyan-glow)]" />
                                    <span>Discipline</span>
                                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--cyan)] shadow-[0_0_8px_var(--cyan-glow)]" />
                                    <span>Results</span>
                                </div>

                                <p className="text-base md:text-lg text-[var(--text-secondary)] max-w-xl mb-9 leading-relaxed">
                                    {s.sub}
                                </p>
                                <div className="flex flex-wrap gap-3 mb-9">
                                    <Link to={s.cta_href} className="btn-solid" data-testid={`hero-cta-${s.key}`}>
                                        {s.cta_label} <ArrowRight size={16} />
                                    </Link>
                                    {s.secondary_cta_label && s.secondary_cta_href && (
                                        <Link to={s.secondary_cta_href} className="btn-neon" data-testid={`hero-secondary-cta-${s.key}`}>
                                            {s.secondary_cta_label}
                                        </Link>
                                    )}
                                </div>

                                {/* Technical spec strip (carbon-fibre panel) */}
                                <div className="cfibre-panel-cyan rounded-sm grid grid-cols-3 max-w-xl">
                                    {[
                                        { l: "Manufacturing", v: "ISO Certified" },
                                        { l: "Dispatch",       v: "Sydney AU" },
                                        { l: "Documentation",  v: "COA / Lot" },
                                    ].map((it, idx) => (
                                        <div key={it.l} className={`px-4 py-3 ${idx > 0 ? "border-l border-white/5" : ""}`}>
                                            <div className="text-[9px] font-mono uppercase tracking-[0.24em] text-[var(--cyan)]">{it.l}</div>
                                            <div className="text-[13px] font-display font-bold text-white mt-0.5">{it.v}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* RIGHT — mascot */}
                    <div className="lg:col-span-6 order-1 lg:order-2 relative flex items-center justify-center">
                        {/* Cyan glow behind */}
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background: "radial-gradient(circle at 50% 50%, rgba(0,229,255,0.28) 0%, rgba(0,229,255,0.08) 30%, transparent 60%)",
                                filter: "blur(20px)",
                            }}
                        />
                        <img
                            src="/brand/mascot.png"
                            alt="Carbon Labs mascot — carbon-fibre engineered laboratory research"
                            className="relative z-10 w-full max-w-[560px] drop-shadow-[0_0_40px_rgba(0,229,255,0.35)]"
                            style={{ mixBlendMode: "screen" }}
                            data-testid="hero-mascot"
                            loading="eager"
                        />
                        {/* Tiny technical caption */}
                        <div className="hidden md:flex absolute bottom-4 right-4 items-center gap-2 z-20">
                            <span className="w-2 h-2 rounded-full bg-[var(--cyan)] animate-pulse shadow-[0_0_8px_var(--cyan-glow)]" />
                            <span className="text-[9px] font-mono uppercase tracking-[0.32em] text-[var(--silver-mid)]">
                                CL / MASCOT · 001
                            </span>
                        </div>
                    </div>
                </div>
                {list.length > 1 && (
                    <div className="flex items-center gap-4 mt-12">
                        <button onClick={() => goTo(index - 1)} className="p-2 rounded-full glass border border-white/10 hover:border-[var(--tiffany)] hover:text-[var(--tiffany)] transition-colors" aria-label="Previous slide" data-testid="hero-prev">
                            <ChevronLeft size={16} />
                        </button>
                        <div className="flex items-center gap-2" data-testid="hero-indicators">
                            {list.map((s, i) => (
                                <button key={s.key} onClick={() => goTo(i)} className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-10 bg-[var(--tiffany)] shadow-[0_0_8px_var(--tiffany)]" : "w-2.5 bg-white/20 hover:bg-white/40"}`} aria-label={`Go to slide ${i + 1}`} data-testid={`hero-dot-${i}`} />
                            ))}
                        </div>
                        <button onClick={() => goTo(index + 1)} className="p-2 rounded-full glass border border-white/10 hover:border-[var(--tiffany)] hover:text-[var(--tiffany)] transition-colors" aria-label="Next slide" data-testid="hero-next">
                            <ChevronRight size={16} />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

/* ───────────────────────────── Live Feed Ticker ───────────────────────────── */

const FEED_ICONS = { truck: Truck, sparkles: Sparkles, tag: Tag, headset: Headset, package: Package2, award: Award };

const LiveFeed = ({ messages }) => {
    if (!messages || messages.length === 0) return null;
    const items = [...messages, ...messages];
    return (
        <div className="border-y border-white/5 bg-black/40 py-3 overflow-hidden relative" data-testid="live-feed">
            <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none bg-gradient-to-r from-[#0A0604] to-transparent" />
            <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none bg-gradient-to-l from-[#0A0604] to-transparent" />
            <div className="ticker-row">
                {items.map((m, i) => {
                    const Icon = FEED_ICONS[m.icon] || Sparkles;
                    return (
                        <div key={i} className="flex items-center gap-2.5 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)]" data-testid={`feed-item-${i}`}>
                            <Icon size={12} className="text-[var(--tiffany)]" />
                            <span>{m.text}</span>
                            <span className="tiffany-dot opacity-50" />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/* ───────────────────────────── Trust Bar ───────────────────────────── */

const TRUST_ITEMS = [
    { icon: <span className="text-2xl leading-none">AU</span>, label: "Australian Based" },
    { icon: <Truck size={18} />, label: "Fast Dispatch" },
    { icon: <Lock size={18} />, label: "Secure Checkout" },
    { icon: <FileCheck2 size={18} />, label: "Batch Documentation" },
    { icon: <MessageSquare size={18} />, label: "Responsive Support" },
    { icon: <Award size={18} />, label: "Premium Packaging" },
];

const TrustBar = () => (
    <section className="py-12 border-b border-white/5" data-testid="trust-bar">
        <div className="container-x">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {TRUST_ITEMS.map((it, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-4 rounded-2xl glass border border-white/5 hover-lift group" data-testid={`trust-item-${idx}`}>
                        <span className="text-[var(--gold)] group-hover:text-[var(--tiffany)] transition-colors">{it.icon}</span>
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] group-hover:text-white transition-colors">{it.label}</span>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

/* ───────────────────────────── Category Cards (auto from DB) ───────────────────────────── */

const CategoryCards = ({ categories }) => {
    const list = (categories || []);
    return (
        <section className="py-20" data-testid="category-cards">
            <div className="container-x">
                <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div>
                        <span className="eyebrow text-[var(--cyan-hi)]">// Research Areas</span>
                        <h2 className="font-display text-3xl md:text-5xl font-extrabold text-white tracking-tighter mt-2">
                            Explore by <span className="text-gold-gradient">Research Area</span>
                        </h2>
                        <p className="text-sm text-[var(--text-secondary)] mt-3 max-w-md">
                            Prefer to browse by discipline? Every card leads to a lot-traceable, manufacturer-tested subset of the catalogue.
                        </p>
                    </div>
                    <Link to="/shop" className="text-sm font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--tiffany)] transition-colors flex items-center gap-2" data-testid="category-view-all">
                        Shop all <ArrowRight size={14} />
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {list.map((cat) => {
                        const Icon = CATEGORY_ICON[cat.slug] || FlaskConical;
                        return (
                            <Link
                                key={cat.slug}
                                to={`/shop?category=${cat.slug}`}
                                className="relative group rounded-[18px] p-7 border border-white/5 bg-gradient-to-br from-[rgba(31,20,16,0.7)] to-[rgba(10,6,4,0.95)] hover-lift overflow-hidden"
                                data-testid={`category-card-${cat.slug}`}
                            >
                                <div className="absolute -top-20 -right-20 w-52 h-52 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: "radial-gradient(circle, var(--tiffany-soft), transparent 70%)" }} />
                                <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 bg-[rgba(233,188,11,0.08)] border border-[rgba(233,188,11,0.2)] group-hover:border-[var(--tiffany)] transition-colors">
                                    <Icon size={22} className="text-[var(--gold)] group-hover:text-[var(--tiffany)] transition-colors" />
                                </div>
                                <h3 className="font-display text-xl font-bold text-white mb-2 group-hover:text-[var(--gold)] transition-colors">{cat.name}</h3>
                                {cat.description && (
                                    <p className="text-sm text-[var(--text-secondary)] leading-relaxed line-clamp-2">{cat.description}</p>
                                )}
                                <div className="mt-6 flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] group-hover:text-[var(--tiffany)] transition-colors">
                                    Explore <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                                </div>
                            </Link>
                        );
                    })}
                    {/* Static COA Library card */}
                    <Link
                        to="/coa-library"
                        className="relative group rounded-[18px] p-7 border border-white/5 bg-gradient-to-br from-[rgba(31,20,16,0.7)] to-[rgba(10,6,4,0.95)] hover-lift overflow-hidden"
                        data-testid="category-card-coa-library"
                    >
                        <div className="absolute -top-20 -right-20 w-52 h-52 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: "radial-gradient(circle, var(--tiffany-soft), transparent 70%)" }} />
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-5 bg-[rgba(233,188,11,0.08)] border border-[rgba(233,188,11,0.2)] group-hover:border-[var(--tiffany)] transition-colors">
                            <FileCheck2 size={22} className="text-[var(--gold)] group-hover:text-[var(--tiffany)] transition-colors" />
                        </div>
                        <h3 className="font-display text-xl font-bold text-white mb-2 group-hover:text-[var(--gold)] transition-colors">COA Library</h3>
                        <p className="text-sm text-[var(--text-secondary)] leading-relaxed line-clamp-2">Manufacturer-supplied Certificates of Analysis for every batch.</p>
                        <div className="mt-6 flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] group-hover:text-[var(--tiffany)] transition-colors">
                            View library <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                        </div>
                    </Link>
                </div>
            </div>
        </section>
    );
};

/* ───────────────────────────── How It Works ───────────────────────────── */

const HowItWorks = ({ steps }) => {
    const list = (steps || []).filter((s) => s.active !== false);
    if (list.length === 0) return null;
    return (
        <section className="py-20 border-t border-white/5" data-testid="how-it-works">
            <div className="container-x">
                <div className="text-center mb-14">
                    <span className="eyebrow text-[var(--tiffany)]">// How It Works</span>
                    <h2 className="font-display text-3xl md:text-5xl font-extrabold text-white tracking-tighter mt-2">
                        From browse to <span className="text-gold-gradient">bench</span>
                    </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 relative">
                    {list.map((s, i) => {
                        const Icon = IconOf(s.icon);
                        return (
                            <div key={i} className="relative p-6 rounded-[18px] glass border border-white/5 hover-lift" data-testid={`how-step-${i}`}>
                                <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-[var(--gold)] text-black font-mono font-black text-sm flex items-center justify-center shadow-[0_0_20px_rgba(233,188,11,0.4)]">
                                    {s.step ?? (i + 1)}
                                </div>
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 mt-2 bg-[rgba(79,209,197,0.08)] border border-[rgba(79,209,197,0.2)]">
                                    <Icon size={18} className="text-[var(--tiffany)]" />
                                </div>
                                <h3 className="font-display text-lg font-bold text-white mb-2">{s.title}</h3>
                                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{s.desc}</p>
                            </div>
                        );
                    })}
                </div>
                <div className="text-center mt-12">
                    <Link to="/shop" className="btn-solid pulse-glow" data-testid="how-it-works-cta">
                        Shop All Categories <ArrowRight size={16} />
                    </Link>
                </div>
            </div>
        </section>
    );
};

/* ───────────────────────────── Why Choose ───────────────────────────── */

const WhyChoose = ({ cards }) => {
    const list = (cards || []).filter((c) => c.active !== false);
    if (list.length === 0) return null;
    return (
        <section className="py-20 border-t border-white/5" data-testid="why-choose">
            <div className="container-x">
                <div className="text-center mb-12">
                    <span className="eyebrow text-[var(--tiffany)]">// Why Carbon</span>
                    <h2 className="font-display text-3xl md:text-5xl font-extrabold text-white tracking-tighter mt-2">
                        Why Choose <span className="text-gold-gradient">Carbon Labs</span>
                    </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {list.map((c, i) => {
                        const Icon = IconOf(c.icon);
                        return (
                            <div key={i} className="p-6 rounded-[18px] glass border border-white/5 hover-lift group" data-testid={`why-card-${i}`}>
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 bg-[rgba(233,188,11,0.08)] border border-[rgba(233,188,11,0.2)] group-hover:border-[var(--tiffany)] transition-colors">
                                    <Icon size={18} className="text-[var(--gold)] group-hover:text-[var(--tiffany)] transition-colors" />
                                </div>
                                <h3 className="font-display text-lg font-bold text-white mb-2">{c.title}</h3>
                                <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{c.desc}</p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

/* ───────────────────────────── Customer Reviews ───────────────────────────── */

const ReviewsCarousel = ({ reviews }) => {
    const [index, setIndex] = useState(0);
    const scrollRef = useRef(null);
    if (!reviews || reviews.length === 0) return null;

    const goTo = (i) => {
        const next = (i + reviews.length) % reviews.length;
        setIndex(next);
        scrollRef.current?.scrollTo({ left: next * (scrollRef.current.clientWidth * 0.85), behavior: "smooth" });
    };

    return (
        <section className="py-20 border-t border-white/5" data-testid="reviews-section">
            <div className="container-x">
                <div className="text-center mb-12">
                    <span className="eyebrow text-[var(--tiffany)]">// Researcher Voices</span>
                    <h2 className="font-display text-3xl md:text-5xl font-extrabold text-white tracking-tighter mt-2">
                        What researchers <span className="text-gold-gradient">say</span>
                    </h2>
                </div>
                <div
                    ref={scrollRef}
                    className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4 scrollbar-hide"
                    style={{ scrollbarWidth: "none" }}
                    data-testid="reviews-scroller"
                >
                    {reviews.map((r, i) => (
                        <article
                            key={r.id || i}
                            className="snap-start shrink-0 w-[85%] sm:w-[420px] p-6 rounded-[18px] glass border border-white/5 hover-lift relative"
                            data-testid={`review-card-${i}`}
                        >
                            <Quote size={26} className="text-[var(--tiffany)] opacity-50 mb-3" />
                            <div className="flex items-center gap-3 mb-3">
                                <StarRating value={r.rating || 5} size={14} />
                                {r.is_verified && (
                                    <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--tiffany)] flex items-center gap-1">
                                        <ShieldCheck size={11} /> Verified
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-white leading-relaxed mb-4 line-clamp-5">
                                {r.body}
                            </p>
                            <div className="pt-4 border-t border-white/5">
                                <div className="text-sm font-semibold text-white">{r.customer_name || "Verified researcher"}</div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                    Verified Carbon Labs customer
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
                {reviews.length > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-8">
                        <button onClick={() => goTo(index - 1)} className="p-2 rounded-full glass border border-white/10 hover:border-[var(--tiffany)] hover:text-[var(--tiffany)] transition-colors" aria-label="Previous review" data-testid="review-prev">
                            <ChevronLeft size={16} />
                        </button>
                        <div className="flex items-center gap-1.5">
                            {reviews.map((_, i) => (
                                <button key={i} onClick={() => goTo(i)} className={`h-1.5 rounded-full transition-all ${i === index ? "w-8 bg-[var(--tiffany)]" : "w-2 bg-white/20"}`} aria-label={`Review ${i + 1}`} />
                            ))}
                        </div>
                        <button onClick={() => goTo(index + 1)} className="p-2 rounded-full glass border border-white/10 hover:border-[var(--tiffany)] hover:text-[var(--tiffany)] transition-colors" aria-label="Next review" data-testid="review-next">
                            <ChevronRight size={16} />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

/* ───────────────────────────── FAQ ───────────────────────────── */

const FaqAccordion = ({ items }) => {
    const list = (items || []).filter((f) => f.active !== false);
    const [open, setOpen] = useState(null);
    if (list.length === 0) return null;
    return (
        <section className="py-20 border-t border-white/5" data-testid="home-faq">
            <div className="container-x max-w-3xl">
                <div className="text-center mb-10">
                    <span className="eyebrow text-[var(--tiffany)]">// FAQ</span>
                    <h2 className="font-display text-3xl md:text-5xl font-extrabold text-white tracking-tighter mt-2">
                        Frequently <span className="text-gold-gradient">Asked</span>
                    </h2>
                </div>
                <div className="space-y-3">
                    {list.map((f, i) => {
                        const isOpen = open === i;
                        return (
                            <div key={i} className="rounded-2xl glass border border-white/5 overflow-hidden" data-testid={`faq-item-${i}`}>
                                <button
                                    onClick={() => setOpen(isOpen ? null : i)}
                                    className="w-full flex items-center justify-between gap-4 p-5 text-left group"
                                    data-testid={`faq-toggle-${i}`}
                                    aria-expanded={isOpen}
                                >
                                    <span className="text-sm md:text-base font-display font-semibold text-white group-hover:text-[var(--gold)] transition-colors">{f.q}</span>
                                    <ChevronDown size={18} className={`shrink-0 text-[var(--gold)] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                                </button>
                                <div
                                    className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                                >
                                    <div className="overflow-hidden">
                                        <p className="px-5 pb-5 text-sm text-[var(--text-secondary)] leading-relaxed">{f.a}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="text-center mt-8">
                    <Link to="/faq" className="text-sm font-mono uppercase tracking-widest text-[var(--tiffany)] hover:text-white transition-colors inline-flex items-center gap-2" data-testid="faq-view-all">
                        Read the full FAQ <ArrowRight size={14} />
                    </Link>
                </div>
            </div>
        </section>
    );
};

/* ───────────────────────────── Carbon Community ───────────────────────────── */

const SOCIALS = [
    {
        key: "tiktok", label: "TikTok", handle: "Coming soon",
        href: "#",
        icon: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.16a8.16 8.16 0 0 0 4.77 1.52V6.69h-1.84Z" /></svg>),
    },
    {
        key: "facebook", label: "Facebook", handle: "Coming soon",
        href: "#",
        icon: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95Z" /></svg>),
    },
    {
        key: "snapchat", label: "Snapchat", handle: "Coming soon",
        href: "#",
        icon: (<svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12.04 2C8.27 2 7.5 5.2 7.5 7.1c0 .68.1 1.7.2 2.43-.32.12-.64.16-.96.06-.5-.18-.98-.55-1.36-.55-.5 0-.96.4-.96.85 0 .55.66.78 1.32 1.05.9.37 1.9.83 2.36 2.16.06.2.04.42-.06.62-.42.92-1.66 2.78-3.94 3.16-.34.06-.6.4-.6.74 0 .46.34.84.85.94 1.26.24 2.06.42 2.32 1.04.06.13.06.27.04.4-.02.18.08.36.26.46.74.4 2.4.62 2.86.62.16 0 .3-.03.42-.07.46-.16.96-.42 2.86-.42 1.9 0 2.4.26 2.86.42.12.04.26.07.42.07.46 0 2.12-.22 2.86-.62.18-.1.28-.28.26-.46-.02-.13-.02-.27.04-.4.26-.62 1.06-.8 2.32-1.04.5-.1.85-.48.85-.94 0-.34-.26-.68-.6-.74-2.28-.38-3.52-2.24-3.94-3.16-.1-.2-.12-.42-.06-.62.46-1.33 1.46-1.79 2.36-2.16.66-.27 1.32-.5 1.32-1.05 0-.45-.46-.85-.96-.85-.38 0-.86.37-1.36.55-.32.1-.64.06-.96-.06.1-.73.2-1.75.2-2.43C16.5 5.2 15.73 2 12.04 2Z" /></svg>),
    },
];

const VortexCommunity = () => (
    <section className="py-20" data-testid="vortex-community">
        <div className="container-x">
            <div className="rounded-[20px] p-10 md:p-14 glass-elevated border border-white/5 text-center relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none opacity-40" style={{ background: "radial-gradient(circle at 50% 0%, var(--tiffany-soft), transparent 60%)" }} />
                <div className="relative">
                    <span className="eyebrow text-[var(--tiffany)]">// Stay Connected</span>
                    <h2 className="font-display text-3xl md:text-5xl font-extrabold text-white tracking-tighter mt-2 mb-4">
                        Join the <span className="text-gold-gradient">Carbon Community</span>
                    </h2>
                    <p className="text-base text-[var(--text-secondary)] max-w-xl mx-auto mb-10">
                        Stay connected for product announcements, promotions, behind-the-scenes content and community updates.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-4">
                        {SOCIALS.map((s) => (
                            <a key={s.key} href={s.href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-5 py-3 rounded-full glass border border-white/10 hover:border-[var(--tiffany)] hover:bg-[var(--tiffany-soft)] transition-all group" data-testid={`social-${s.key}`}>
                                <span className="text-[var(--gold)] group-hover:text-[var(--tiffany)] transition-colors">{s.icon}</span>
                                <span className="text-left">
                                    <span className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{s.label}</span>
                                    <span className="block text-sm font-semibold text-white">{s.handle}</span>
                                </span>
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </section>
);

/* ───────────────────────────── Newsletter ───────────────────────────── */

const NewsletterStrip = () => {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setSubmitting(true);
        try {
            const res = await api.post("/newsletter", { email });
            toast.success(res.data.message || "Subscribed");
            setEmail("");
        } catch {
            toast.error("Invalid email address");
        }
        setSubmitting(false);
    };

    return (
        <section className="py-20 border-t border-white/5" data-testid="newsletter-strip">
            <div className="container-x">
                <div className="grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <span className="eyebrow text-[var(--tiffany)]">// Stay Updated</span>
                        <h2 className="font-display text-3xl md:text-5xl font-extrabold text-white tracking-tighter mt-2 mb-4">
                            Stay <span className="text-gold-gradient">Updated</span>
                        </h2>
                        <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
                            <li className="flex items-center gap-2"><span className="tiffany-dot" /> Restocks the moment they land</li>
                            <li className="flex items-center gap-2"><span className="tiffany-dot" /> Subscriber-only promotions</li>
                            <li className="flex items-center gap-2"><span className="tiffany-dot" /> New arrivals + research notes</li>
                            <li className="flex items-center gap-2"><span className="tiffany-dot" /> Product announcements</li>
                        </ul>
                    </div>
                    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-strip-form">
                        <input type="email" required placeholder="researcher@lab.com.au" value={email} onChange={(e) => setEmail(e.target.value)} className="flex-1 px-5 py-3 bg-[#140A05] border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--tiffany)] transition-colors" data-testid="newsletter-strip-email" />
                        <button type="submit" disabled={submitting} className="btn-solid" data-testid="newsletter-strip-submit">
                            Subscribe <ArrowRight size={14} />
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
};

/* ───────────────────────────── HomePage ───────────────────────────── */

export const HomePage = () => {
    const [reviews, setReviews] = useState([]);
    const [slides, setSlides] = useState([]);
    const [feed, setFeed] = useState([]);
    const [categories, setCategories] = useState([]);
    const [sections, setSections] = useState(null);

    useSEO({
        title: "Carbon Labs — Premium Research Peptides Australia · Manufacturer-Supplied COA · 99% Purity",
        description: "Australian-based premium research peptides. Manufacturer-supplied Certificate of Analysis per batch, cold-chain dispatched from Sydney.",
        image: "https://carbonlabsau.com/products/klow.png",
        jsonLd: {
            "@context": "https://schema.org", "@type": "Organization", "name": "Carbon Labs",
            "url": "https://carbonlabsau.com", "email": "carbonlabsau@outlook.com",
            "description": "Premium Australian research peptides supplier", "areaServed": "AU",
        },
    });

    useEffect(() => {
        api.get("/home/reviews", { params: { limit: 8 } }).then((r) => setReviews(Array.isArray(r.data) ? r.data : [])).catch(() => setReviews([]));
        api.get("/home/slides").then((r) => setSlides(r.data?.slides || [])).catch(() => setSlides([]));
        api.get("/home/feed").then((r) => setFeed(r.data?.messages || [])).catch(() => setFeed([]));
        api.get("/categories").then((r) => setCategories(Array.isArray(r.data) ? r.data : [])).catch(() => setCategories([]));
        api.get("/home/sections").then((r) => setSections(r.data)).catch(() => setSections(null));
    }, []);

    if (!sections) {
        return (
            <div data-testid="home-page-loading" className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const visible = sections.visible || {};
    const order = sections.order || [];

    const renderSection = (key) => {
        if (visible[key] === false) return null;
        switch (key) {
            case "announcement": return <AnnouncementBanner key="announcement" />;
            case "hero": return (
                <React.Fragment key="hero">
                    {/* Mobile: dedicated Carbon Labs mascot hero (matches
                     * the current promo mockup). Desktop: keep the CMS
                     * hero carousel so promos stay editable. */}
                    <MobileCarbonHero />
                    <div className="hidden lg:block">
                        <HeroCarousel slides={slides} />
                    </div>
                    {/* Frictionless first-time-visitor gateway. Sits
                     * directly under the hero so a new visitor can reach
                     * every product in one tap. */}
                    <ShopGateway />
                </React.Fragment>
            );
            case "live_feed": return <LiveFeed key="live_feed" messages={feed} />;
            case "trust_bar": return <TrustBar key="trust_bar" />;
            case "categories": return <CategoryCards key="categories" categories={categories} />;
            case "how_it_works": return <HowItWorks key="how_it_works" steps={sections.how_it_works} />;
            case "why_choose": return <WhyChoose key="why_choose" cards={sections.why_choose} />;
            case "reviews": return <ReviewsCarousel key="reviews" reviews={reviews} />;
            case "faq": return <FaqAccordion key="faq" items={sections.faq} />;
            case "community": return <VortexCommunity key="community" />;
            case "newsletter": return <NewsletterStrip key="newsletter" />;
            default: return null;
        }
    };

    // Inject the mascot showcase mid-page (between categories and how_it_works
    // when present, otherwise at the array midpoint). This is a fixed-position
    // hero-to-below-fold anchor for the Carbon Labs brand — not CMS-editable.
    const injectIdx = (() => {
        const preferred = order.findIndex((k) => k === "how_it_works");
        if (preferred > 0) return preferred;
        const cats = order.findIndex((k) => k === "categories");
        if (cats >= 0) return cats + 1;
        return Math.max(1, Math.floor(order.length / 2));
    })();

    return (
        <div data-testid="home-page">
            {order.slice(0, injectIdx).map(renderSection)}
            <MascotShowcase />
            {order.slice(injectIdx).map(renderSection)}
        </div>
    );
};
